import {
  // Heading,
  TextField,
  Textarea,
  Select,
  SelectContent,
  SelectTrigger,
  SelectItem,
  Icon,
} from "@elaniintc/goes-design-system";
import { useDigitalSignatureNode } from "./hooks";
import { SignersList } from "./components/SignersList";
import { MultiSelectDropdown } from "./components/MultiSelectDropdown";
import { useState } from "react";

export const DigitalSignatureNode = () => {
  const [searchQuery, setSearchQuery] = useState("");

  const {
    // Form fields
    title,
    setTitle,
    description,
    setDescription,
    // variableName,
    // setVariableName,

    // Signature type
    selectedSignatureType,
    setSelectedSignatureType,
    signatureTypes,
    isLoadingSignatureTypes,
    signatureTypesError,

    // Files
    selectedFiles,
    setSelectedFiles,
    availableFiles,
    documentGroups,
    isLoadingFiles,
    filesError,

    // Signers
    selectedSigners,
    setSelectedSigners,
    addSigner,
    removeSigner,
    updateSigner,
    isLoadingSigners,
    signersError,
  } = useDigitalSignatureNode();

  const getSignatureTypeDisplayContent = () => {
    if (isLoadingSignatureTypes) {
      return (
        <div className="w-full flex gap-3 items-center">
          <Icon
            type="far"
            name="spinner"
            className="text-body-subtle animate-spin"
          />
          <span className="text-body-subtle">Cargando tipos de firma...</span>
        </div>
      );
    }

    if (signatureTypesError) {
      return (
        <div className="w-full flex gap-3 items-center">
          <Icon
            type="far"
            name="exclamation-triangle"
            className="text-danger"
          />
          <span className="text-danger">Error al cargar tipos</span>
        </div>
      );
    }

    if (selectedSignatureType) {
      return (
        <div className="w-full flex gap-3 items-center">
          <Icon
            type="far"
            name={selectedSignatureType.icon}
            className="text-body"
          />
          <span className="text-body">{selectedSignatureType.name}</span>
        </div>
      );
    }

    return (
      <span className="text-body-subtle">Selecciona el tipo de firma</span>
    );
  };

  const handleDocumentSelectionChange = (selectedIds: string[]) => {
    // Convert selected IDs back to FileToSign objects
    const selectedDocuments = availableFiles.filter((file) =>
      selectedIds.includes(file.id),
    );
    setSelectedFiles(selectedDocuments);
  };

  const getSelectedDocumentIds = () => {
    return selectedFiles.map((file) => file.id);
  };

  return (
    <div className="py-3 flex flex-col gap-3">
      {/* Sección de configuración básica: Título, Descripción y Tipo de firma */}
      <div className="w-full pt-3 pb-6 px-4 border-b border-neutral-quiet flex flex-col gap-4">
        {/* Título de la firma */}
        <TextField
          label="Título de la firma"
          required
          placeholder="Escribe el título de la firma"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={255}
        />

        {/* Descripción de la firma */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <label className="font-semibold text-body-loud leading-l">
              Descripción de la firma
            </label>
            <span className="text-body-quiet text-sm">Opcional</span>
          </div>
          <Textarea
            placeholder="Describe el propósito de la firma"
            value={description}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setDescription(e.target.value)
            }
            rows={3}
            maxLength={500}
          />
        </div>

        {/* Tipo de firma */}
        <div className="flex flex-col gap-2">
          <label className="font-semibold text-body-loud leading-l">
            Tipo de firma <span className="text-danger">*</span>
          </label>
          <Select
            onValueChange={(value: string) => {
              if (value === "no-types") return;
              const type = signatureTypes.find((t) => t.id === value) || null;
              setSelectedSignatureType(type);
            }}
            value={selectedSignatureType?.id ?? undefined}
            disabled={isLoadingSignatureTypes || !!signatureTypesError}
          >
            <SelectTrigger className="min-w-0 w-full flex">
              {getSignatureTypeDisplayContent()}
            </SelectTrigger>
            <SelectContent className="max-h-[440px] overflow-y-auto border border-neutral py-2">
              {signatureTypes.length > 0 ? (
                signatureTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    <div className="w-full flex items-center gap-3">
                      <Icon type="far" name={type.icon} className="text-body" />
                      <div className="flex flex-col">
                        <span>{type.name}</span>
                      </div>
                    </div>
                  </SelectItem>
                ))
              ) : (
                <SelectItem value="no-types" disabled>
                  <span className="text-body-subtle">
                    No hay tipos de firma disponibles
                  </span>
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Archivos para firmar */}
      <div className="w-full pt-3 pb-6 px-4 border-b border-neutral-quiet">
        <div className="flex flex-col gap-2">
          <label className="font-semibold text-body-loud leading-l">
            Archivos para firmar <span className="text-danger">*</span>
          </label>
          <MultiSelectDropdown
            options={documentGroups}
            placeholder="Selecciona los archivos a firmar"
            selectedItems={getSelectedDocumentIds()}
            onSelectionChange={handleDocumentSelectionChange}
            disabled={isLoadingFiles || !!filesError}
            search={{
              placeholder: "Buscar archivos",
              query: searchQuery,
              onSearch: setSearchQuery,
            }}
          />
          {selectedFiles.length > 0 && (
            <div className="text-sm text-body-quiet">
              {selectedFiles.length} archivo
              {selectedFiles.length > 1 ? "s" : ""} seleccionado
              {selectedFiles.length > 1 ? "s" : ""}
            </div>
          )}
        </div>
      </div>

      {/* Firmantes */}
      <SignersList
        selectedSigners={selectedSigners}
        isLoadingSigners={isLoadingSigners}
        signersError={signersError}
        onAddSigner={addSigner}
        onRemoveSigner={removeSigner}
        onUpdateSigner={updateSigner}
        setSelectedSigners={setSelectedSigners}
      />
    </div>
  );
};
